import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  optionalServices: boolean;
}

interface CookieConsentContextType {
  hasConsent: boolean;
  preferences: CookiePreferences;
  showBanner: boolean;
  showSettingsModal: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  updatePreferences: (preferences: Partial<CookiePreferences>) => void;
  showSettings: () => void;
  hideSettings: () => void;
  hideBanner: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const COOKIE_CONSENT_KEY = 'soulminter-cookie-consent';
const COOKIE_PREFERENCES_KEY = 'soulminter-cookie-preferences';

const defaultPreferences: CookiePreferences = {
  essential: true, // Always true, cannot be disabled
  analytics: false,
  optionalServices: false,
};

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [hasConsent, setHasConsent] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (consent === 'true' && savedPreferences) {
      try {
        const parsedPreferences = JSON.parse(savedPreferences);
        setPreferences({ ...defaultPreferences, ...parsedPreferences });
        setHasConsent(true);
        setShowBanner(false);
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
  }, []); // Empty dependency array is correct here - we only want this to run once on mount

  const acceptAll = () => {
    const newPreferences: CookiePreferences = {
      essential: true,
      analytics: true,
      optionalServices: true,
    };
    
    setPreferences(newPreferences);
    setHasConsent(true);
    setShowBanner(false);
    
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPreferences));
    
    // Initialize analytics if accepted
    if (newPreferences.analytics) {
      initializeAnalytics();
    }
  };

  const rejectAll = () => {
    const newPreferences: CookiePreferences = {
      essential: true,
      analytics: false,
      optionalServices: false,
    };

    setPreferences(newPreferences);
    setHasConsent(true);
    setShowBanner(false);

    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPreferences));

    // Disable analytics if rejected
    disableAnalytics();
  };

  const updatePreferences = (newPreferences: Partial<CookiePreferences>) => {
    const updatedPreferences = { ...preferences, ...newPreferences };
    setPreferences(updatedPreferences);
    setHasConsent(true);
    setShowBanner(false);
    
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(updatedPreferences));
    
    // Handle analytics based on preference
    if (updatedPreferences.analytics) {
      initializeAnalytics();
    } else {
      disableAnalytics();
    }
  };

  const showSettings = () => {
    setShowSettingsModal(true);
  };

  const hideSettings = () => {
    setShowSettingsModal(false);
  };

  const hideBanner = () => {
    setShowBanner(false);
  };

  const initializeAnalytics = () => {
    // Vercel Analytics and Google Analytics initialization
    if (typeof window !== 'undefined') {
      // Set consent for gtag if available (Google Analytics)
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'denied',
          functionality_storage: 'granted',
          personalization_storage: 'denied',
          security_storage: 'granted',
        });
      }
      
      // Enable Vercel Analytics by removing the opt-out flag
      localStorage.removeItem('vercel-analytics-opt-out');
    }
  };

  const disableAnalytics = () => {
    // Disable Vercel Analytics and Google Analytics
    if (typeof window !== 'undefined') {
      // Set consent for gtag if available (Google Analytics)
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          functionality_storage: 'granted',
          personalization_storage: 'denied',
          security_storage: 'granted',
        });
      }
      
      // Disable Vercel Analytics by setting opt-out flag
      localStorage.setItem('vercel-analytics-opt-out', 'true');
    }
  };

  const value: CookieConsentContextType = {
    hasConsent,
    preferences,
    showBanner,
    showSettingsModal,
    acceptAll,
    rejectAll,
    updatePreferences,
    showSettings,
    hideSettings,
    hideBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}
